/**
 * Aim at the man under the cursor (ROUND7 §2). The cursor is read on the ground (y = 0) but a soldier is drawn
 * standing up from his feet, so at the game's 45 deg a cursor on his chest lands 1.2 m north of him - behind him,
 * seen from the south - and a stab or a throw aimed there goes into the air. This finds the man whose drawn
 * silhouette holds the cursor and hands back his feet. Pure: the camera comes in as `factor` (1 / tan(pitch)),
 * read from the camera itself, never assumed.
 */

/** A man the cursor can land on: where his feet stand. */
export interface AimBody {
  x: number;
  z: number;
  /** Height of the ground under his feet above y = 0, metres. Absent means 0. */
  y?: number;
  /** Drawn height and half-width when it is not a man (a searchlight pole, a dog); absent means a man's. */
  height?: number;
  halfWidth?: number;
  /**
   * Slack at both ends of its drawn height, metres; absent means a man's `BODY_SLACK`. ROUND8: a dog is longer than
   * it is tall, so facing the camera its nose or its tail reaches half its length past where it stands.
   */
  slack?: number;
}

/** Half the width of a standing man as drawn, metres. */
export const BODY_HALF_WIDTH = 0.45;

/**
 * His drawn height, feet to crown, metres: the rig's 1.94 m at the characters' 1.18 drawing scale. It was 1.8,
 * and a cursor on a man's helmet missed him - the aim audit's rifleman at nine tenths of his height.
 */
export const BODY_HEIGHT = 2.3;

/** Slack added at both ends of his drawn height (boots, the body's own depth), metres. */
export const BODY_SLACK = 0.2;

/** 1 / tan(pitch), from the camera's own sine and cosine of its pitch: cosPitch / sinPitch. */
export function groundFactor(camera: { readonly sinPitch: number; readonly cosPitch: number }): number {
  return camera.cosPitch / camera.sinPitch;
}

/** Where a point `y` metres above ground point (x, z) is drawn, read on the y = 0 plane: { x, z: z - y * factor }. */
export function drawnAt(x: number, z: number, y: number, factor: number): { x: number; z: number } {
  return { x, z: z - y * factor };
}

/** The feet of the man whose drawn silhouette holds `cursor`, else null. */
export function aimPick(
  cursor: { readonly x: number; readonly z: number },
  bodies: readonly AimBody[],
  factor: number,
): { x: number; z: number } | null {
  if (!Number.isFinite(factor) || factor < 0) return null;
  let best: AimBody | null = null;
  for (const b of bodies) {
    const g = b.y ?? 0;
    const footZ = b.z - g * factor;
    const headZ = b.z - (g + (b.height ?? BODY_HEIGHT)) * factor;
    const slack = b.slack ?? BODY_SLACK;
    const contains =
      Math.abs(cursor.x - b.x) <= (b.halfWidth ?? BODY_HALF_WIDTH) &&
      cursor.z <= footZ + slack &&
      cursor.z >= headZ - slack;
    if (!contains) continue;
    // Larger z is nearer the camera: that man is drawn over the other, so he is the one the cursor is on.
    if (best === null || b.z > best.z) best = b;
  }
  if (best === null) return null;
  return { x: best.x, z: best.z };
}
